import React, { useState } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import type { ImageData } from "src/vo/userVo";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;

interface ImageSliderProps {
  images: ImageData[];
  className?: string;
}

const ImageSlider: React.FC<ImageSliderProps> = ({ images, className }) => {
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set());

  const handleImageError = (imgIdx: number) => {
    setImageLoadErrors(prev => new Set([...Array.from(prev), imgIdx]));
  };

  // 이미지가 없으면 아무것도 렌더링하지 않음
  if (!images || images.length === 0) {
    return null;
  }

  // 슬라이드 설정
  const settings = {
    dots: true,
    arrows: true,
    infinite: images.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    swipeToSlide: true,
    touchMove: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
        }
      }
    ]
  };

  return (
    <SliderContainer className={className}>
      <StyledSlider {...settings}>
        {images.map((image) => (
          <SlideItem key={`img-${image.img_idx}`}>
            <ImageContainer>
              {imageLoadErrors.has(image.img_idx) ? (
                <ErrorImage>
                  <p>이미지를 불러올 수 없습니다.</p>
                </ErrorImage>
              ) : (
                <StoreImage
                  src={imageServerIp + image.img_url}
                  alt={image.img_origin_name || `매장 이미지 ${image.img_idx}`}
                  onError={() => handleImageError(image.img_idx)}
                  loading="lazy"
                />
              )}
            </ImageContainer>
          </SlideItem>
        ))}
      </StyledSlider>
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  background-color: #f5f5f5;

  .slick-slider {
    height: 100%;
  }

  .slick-list {
    height: 100%;
  }

  .slick-track {
    height: 100%;
  }

  .slick-slide {
    height: 240px;
  }

  .slick-slide > div {
    height: 100%;
  }

  ${/* 인디케이터 스타일 */ ""}
  .slick-dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex !important;
    justify-content: center;
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 8px;

    li {
      width: 8px;
      height: 8px;
      margin: 0;

      button {
        width: 8px;
        height: 8px;
        padding: 0;
        border: none;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.5);
        font-size: 0;
        cursor: pointer;
        transition: all 0.3s ease;

        &:before {
          display: none;
        }
      }

      &.slick-active button {
        background-color: rgba(255, 255, 255, 1);
        transform: scale(1.2);
      }
    }
  }

  ${/* 좌우 화살표 스타일 */ ""}
  .slick-prev,
  .slick-next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 0;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.7);
    }

    &:before {
      font-family: inherit;
      font-size: 18px;
      line-height: 1;
      opacity: 1;
      color: white;
    }
  }

  .slick-prev {
    left: 16px;

    &:before {
      content: "‹";
    }
  }

  .slick-next {
    right: 16px;

    &:before {
      content: "›";
    }
  }

  @media (max-width: 768px) {
    .slick-prev,
    .slick-next {
      display: none !important;
    }
  }
`;

const StyledSlider = styled(Slider)`
  height: 100%;
`;

const SlideItem = styled.div`
  height: 240px;
  outline: none;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StoreImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const ErrorImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0;
  color: #999;

  p {
    margin: 0;
    font-size: 14px;
  }
`;


export default ImageSlider;